import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined"
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined"
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined"
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded"
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import { useLocalization } from "@fluent/react"
import { alpha } from "@mui/material/styles"
import { Avatar, Box, Button, Chip, Divider, LinearProgress, Paper, Stack, Typography } from "@mui/material"
import { useMemo } from "react"
import { DocumentStatusChip } from "../controls/DocumentStatusChip"
import { DocumentTypeChip } from "../controls/DocumentTypeChip"

type ReportPriority = "critical" | "high" | "medium"

type ReportDocumentItem = {
	id: number
	documentNumber: string
	title: string
	documentType: string
	status: string
	owner: string
	area: string
	reason: string
	dueDate: string
	daysLeft: number
	progress: number
	priority: ReportPriority
}

const expiringDocuments: ReportDocumentItem[] = [
	{
		id: 1,
		documentNumber: "QMS-INS-014",
		title: "Instrukcja kwalifikacji komór laminarnych",
		documentType: "INS",
		status: "A",
		owner: "Dzial Walidacji",
		area: "Produkcja sterylna",
		reason: "Data waznosci konczy sie za 6 dni",
		dueDate: "2026-04-15",
		daysLeft: 6,
		progress: 88,
		priority: "critical",
	},
	{
		id: 2,
		documentNumber: "QMS-SOP-032",
		title: "Postepowanie z odchyleniami krytycznymi",
		documentType: "SOP",
		status: "A",
		owner: "QA Compliance",
		area: "System jakosci",
		reason: "Data waznosci konczy sie za 18 dni",
		dueDate: "2026-04-27",
		daysLeft: 18,
		progress: 63,
		priority: "high",
	},
	{
		id: 3,
		documentNumber: "QMS-FRM-109",
		title: "Formularz weryfikacji warunkow transportu",
		documentType: "FRM",
		status: "A",
		owner: "Logistyka",
		area: "Magazyn centralny",
		reason: "Data waznosci konczy sie za 29 dni",
		dueDate: "2026-05-08",
		daysLeft: 29,
		progress: 42,
		priority: "medium",
	},
]

const reviewRequiredDocuments: ReportDocumentItem[] = [
	{
		id: 4,
		documentNumber: "QMS-PRO-008",
		title: "Proces zwalniania serii do obrotu",
		documentType: "PRO",
		status: "A",
		owner: "Osoba Wykwalifikowana",
		area: "Batch release",
		reason: "Termin przegladu mija za 3 dni",
		dueDate: "2026-04-12",
		daysLeft: 3,
		progress: 94,
		priority: "critical",
	},
	{
		id: 5,
		documentNumber: "QMS-SOP-054",
		title: "Przeglad trendow srodowiskowych",
		documentType: "SOP",
		status: "A",
		owner: "Mikrobiologia",
		area: "Monitoring srodowiska",
		reason: "Przeglad okresowy wymagany w tym tygodniu",
		dueDate: "2026-04-18",
		daysLeft: 9,
		progress: 72,
		priority: "high",
	},
	{
		id: 6,
		documentNumber: "QMS-WI-021",
		title: "Instrukcja czyszczenia izolatora",
		documentType: "WI",
		status: "A",
		owner: "Aseptyka",
		area: "Obszar A/B",
		reason: "Przeglad po zmianie zakresu procesu",
		dueDate: "2026-04-22",
		daysLeft: 13,
		progress: 57,
		priority: "medium",
	},
]

const formatDisplayDate = (value: string) => new Date(value).toLocaleDateString()

const getPriorityPalette = (priority: ReportPriority) => {
	switch (priority) {
		case "critical":
			return {
				label: "Krytyczny",
				color: "#b42318",
				soft: "#fef3f2",
				border: "#fecdca",
			}
		case "high":
			return {
				label: "Wysoki",
				color: "#b54708",
				soft: "#fff7ed",
				border: "#fed7aa",
			}
		default:
			return {
				label: "Sredni",
				color: "#175cd3",
				soft: "#eff8ff",
				border: "#b2ddff",
			}
	}
}

type SummaryCardProps = {
	icon: React.ReactNode
	label: string
	value: string
	helper: string
	accent: string
}

const SummaryCard = ({ icon, label, value, helper, accent }: SummaryCardProps) => (
	<Paper
		variant="outlined"
		sx={{
			p: 2,
			height: "100%",
			borderRadius: 3,
			borderColor: alpha(accent, 0.24),
			background: `linear-gradient(180deg, ${alpha(accent, 0.1)} 0%, rgba(255,255,255,0.96) 100%)`,
		}}
	>
		<Stack spacing={1.5}>
			<Avatar sx={{ bgcolor: alpha(accent, 0.14), color: accent, width: 42, height: 42 }}>
				{icon}
			</Avatar>
			<Box>
				<Typography variant="caption" color="text.secondary">
					{label}
				</Typography>
				<Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
					{value}
				</Typography>
			</Box>
			<Typography variant="body2" color="text.secondary">
				{helper}
			</Typography>
		</Stack>
	</Paper>
)

type ReportSectionProps = {
	title: string
	subtitle: string
	icon: React.ReactNode
	accent: string
	primaryActionLabel: string
	secondaryActionLabel: string
	items: ReportDocumentItem[]
}

const ReportSection = ({
	title,
	subtitle,
	icon,
	accent,
	primaryActionLabel,
	secondaryActionLabel,
	items,
}: ReportSectionProps) => (
	<Paper
		variant="outlined"
		sx={{
			borderRadius: 4,
			overflow: "hidden",
			borderColor: alpha(accent, 0.22),
			boxShadow: `0 18px 50px ${alpha(accent, 0.08)}`,
		}}
	>
		<Box
			sx={{
				px: 3,
				py: 2.5,
				color: "#132238",
				background: `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, rgba(255,255,255,0.98) 58%, ${alpha(accent, 0.08)} 100%)`,
			}}
		>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar sx={{ bgcolor: alpha(accent, 0.14), color: accent }}>
						{icon}
					</Avatar>
					<Box>
						<Typography variant="h6" fontWeight={800}>
							{title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{subtitle}
						</Typography>
					</Box>
				</Stack>
				<Chip
					label={`${items.length} dokumenty`}
					sx={{
						fontWeight: 700,
						color: accent,
						bgcolor: alpha(accent, 0.1),
						border: `1px solid ${alpha(accent, 0.22)}`,
					}}
				/>
			</Stack>
		</Box>

		<Stack divider={<Divider />}>
			{items.map(item => {
				const priority = getPriorityPalette(item.priority)

				return (
					<Box key={item.id} sx={{ p: 2.5 }}>
						<Stack spacing={2}>
							<Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
								<Stack spacing={1.25} sx={{ minWidth: 0 }}>
									<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
										<Typography variant="subtitle1" fontWeight={800}>
											{item.documentNumber}
										</Typography>
										<DocumentTypeChip docType={item.documentType} />
										<DocumentStatusChip status={item.status} />
										<Chip
											size="small"
											icon={<PriorityHighRoundedIcon sx={{ fontSize: 16 }} />}
											label={priority.label}
											sx={{
												color: priority.color,
												bgcolor: priority.soft,
												border: `1px solid ${priority.border}`,
												fontWeight: 700,
											}}
										/>
									</Stack>

									<Typography variant="h6" sx={{ fontSize: 18, lineHeight: 1.3 }}>
										{item.title}
									</Typography>

									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
										<Chip size="small" label={item.area} variant="outlined" />
										<Chip size="small" label={item.owner} variant="outlined" />
									</Stack>
								</Stack>

								<Paper
									variant="outlined"
									sx={{
										minWidth: { xs: "100%", lg: 220 },
										p: 1.75,
										borderRadius: 3,
										bgcolor: alpha(accent, 0.04),
										borderColor: alpha(accent, 0.18),
									}}
								>
									<Stack spacing={0.75}>
										<Typography variant="caption" color="text.secondary">
											Termin graniczny
										</Typography>
										<Typography variant="h6" fontWeight={800}>
											{formatDisplayDate(item.dueDate)}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pozostalo {item.daysLeft} dni
										</Typography>
										<LinearProgress
											variant="determinate"
											value={item.progress}
											sx={{
												mt: 0.5,
												height: 8,
												borderRadius: 999,
												bgcolor: alpha(accent, 0.12),
												"& .MuiLinearProgress-bar": {
													borderRadius: 999,
													bgcolor: accent,
												},
											}}
										/>
									</Stack>
								</Paper>
							</Stack>

							<Box
								sx={{
									px: 1.5,
									py: 1.25,
									borderRadius: 3,
									bgcolor: priority.soft,
									border: `1px solid ${priority.border}`,
								}}
							>
								<Typography variant="body2" sx={{ color: priority.color, fontWeight: 700 }}>
									{item.reason}
								</Typography>
							</Box>

							<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
								<Typography variant="body2" color="text.secondary">
									Mockup raportowy do szybkiej obslugi dokumentow wymagajacych reakcji.
								</Typography>
								<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
									<Button
										variant="contained"
										startIcon={<AssignmentTurnedInOutlinedIcon />}
										sx={{
											minWidth: 180,
											bgcolor: accent,
											boxShadow: "none",
											"&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
										}}
									>
										{primaryActionLabel}
									</Button>
									<Button
										variant="outlined"
										startIcon={<AutorenewRoundedIcon />}
										sx={{ minWidth: 210, borderColor: alpha(accent, 0.35), color: accent }}
									>
										{secondaryActionLabel}
									</Button>
								</Stack>
							</Stack>
						</Stack>
					</Box>
				)
			})}
		</Stack>
	</Paper>
)

export const QmsReports = () => {
	const { l10n } = useLocalization()

	const stats = useMemo(() => {
		const total = expiringDocuments.length + reviewRequiredDocuments.length
		const critical = [...expiringDocuments, ...reviewRequiredDocuments].filter(item => item.priority === "critical").length

		return {
			total,
			expiring: expiringDocuments.length,
			review: reviewRequiredDocuments.length,
			critical,
		}
	}, [])

	return (
		<Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f7f9fc", minHeight: "100%" }}>
			<Stack spacing={3}>
				<Paper
					elevation={0}
					sx={{
						position: "relative",
						overflow: "hidden",
						borderRadius: 5,
						p: { xs: 2.5, md: 4 },
						color: "#102a43",
						border: "1px solid #d8e6f3",
						background: "linear-gradient(135deg, #fff8ec 0%, #f8fbff 45%, #eef6ff 100%)",
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: -80,
							right: -60,
							width: 240,
							height: 240,
							borderRadius: "50%",
							bgcolor: alpha("#175cd3", 0.08),
						}}
					/>
					<Box
						sx={{
							position: "absolute",
							bottom: -70,
							left: -30,
							width: 200,
							height: 200,
							borderRadius: "50%",
							bgcolor: alpha("#f79009", 0.1),
						}}
					/>

					<Stack spacing={3} sx={{ position: "relative" }}>
						<Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", lg: "center" }}>
							<Box sx={{ maxWidth: 760 }}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
									<InsightsOutlinedIcon sx={{ color: "#175cd3" }} />
									<Typography variant="overline" sx={{ letterSpacing: ".14em", fontWeight: 800, color: "#175cd3" }}>
										{l10n.getString("qms-reports-title", null, "QMS Reports")}
									</Typography>
								</Stack>
								<Typography variant="h4" fontWeight={900} sx={{ mb: 1.25 }}>
									Dokumenty wymagajace reakcji w najblizszych dniach
								</Typography>
								<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
									Mockupowy ekran raportowy pokazujacy dokumenty, ktorym konczy sie waznosc oraz te, ktore wymagaja przegladu. Akcje po prawej pozwalaja szybko zlecic przeglad albo przygotowac nowa wersje dokumentu.
								</Typography>
							</Box>

							<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
								<Button variant="contained" startIcon={<FactCheckOutlinedIcon />} sx={{ minWidth: 190, bgcolor: "#175cd3", boxShadow: "none" }}>
									Zlec przeglady zbiorczo
								</Button>
								<Button variant="outlined" startIcon={<UpdateOutlinedIcon />} sx={{ minWidth: 190, borderColor: alpha("#175cd3", 0.35), color: "#175cd3" }}>
									Generuj plan aktualizacji
								</Button>
							</Stack>
						</Stack>

						<Stack direction={{ xs: "column", md: "row" }} spacing={2}>
							<SummaryCard
								icon={<DescriptionOutlinedIcon />}
								label="Dokumenty do reakcji"
								value={stats.total.toString()}
								helper="Lacznie dokumenty zblizajace sie do terminu waznosci lub przegladu."
								accent="#175cd3"
							/>
							<SummaryCard
								icon={<WarningAmberRoundedIcon />}
								label="Konczy sie waznosc"
								value={stats.expiring.toString()}
								helper="Dokumenty, dla ktorych warto przygotowac nowa wersje lub przedluzenie."
								accent="#f79009"
							/>
							<SummaryCard
								icon={<FactCheckOutlinedIcon />}
								label="Wymagaja przegladu"
								value={stats.review.toString()}
								helper="Pozycje gotowe do uruchomienia procesu przegladu przez wlasciciela."
								accent="#0f766e"
							/>
							<SummaryCard
								icon={<PriorityHighRoundedIcon />}
								label="Priorytet krytyczny"
								value={stats.critical.toString()}
								helper="Dokumenty, ktore powinny trafic do obslugi w pierwszej kolejnosci."
								accent="#b42318"
							/>
						</Stack>
					</Stack>
				</Paper>

				<ReportSection
					title="Konczy sie data waznosci"
					subtitle="Dokumenty, dla ktorych zbliza sie termin utraty waznosci."
					icon={<WarningAmberRoundedIcon />}
					accent="#f79009"
					primaryActionLabel="Wygeneruj nowa wersje"
					secondaryActionLabel="Zlec przeglad"
					items={expiringDocuments}
				/>

				<ReportSection
					title="Dokumenty wymagajace przegladu"
					subtitle="Pozycje wskazane przez harmonogram przegladow albo zmiane procesu."
					icon={<FactCheckOutlinedIcon />}
					accent="#0f766e"
					primaryActionLabel="Zlec przeglad"
					secondaryActionLabel="Wygeneruj nowa wersje"
					items={reviewRequiredDocuments}
				/>
			</Stack>
		</Box>
	)
}

export default QmsReports
